import { Dispatch, SetStateAction, createContext, useState } from "react";

import { SetValue } from "hooks/useLocalstoragePlainText";
import { UseQueryResult } from "react-query";
import {
    CustomerId,
    GetSavedSearchesResponse,
    RenameSearchParams,
    SavedSearchWithMeta,
    Shortlist,
    UserContext,
} from "services/motorpoint/user-context-api/types";

import useSavedSearches from "../hooks/saved-searches/useSavedSearches";
import { useSavedSearchesApi } from "../hooks/saved-searches/useSavedSearchesApi";
import useShortlist from "../hooks/shortlist/useShortlist";
import { useShortlistApi } from "../hooks/shortlist/useShortlistApi";
import useUserContext from "../hooks/user-context/useUserContext";

export type FavouritesContextProviderProps = {
    children: React.ReactNode;
};

export type FavouritesContextProviderContextProps = {
    userContext: {
        userContext: UserContext | undefined;
        userContextId: string;
        friendlyId?: string;
        setUserContextId: SetValue;
        initUserContextForCustomer: (params: CustomerId) => Promise<void>;
        initUserContextAnonymous: () => Promise<void>;
    };
    useShortlist: () => Shortlist | undefined;
    shortlistApi: {
        error: string | null;
        addToList: (stockNumber: number) => Promise<Shortlist | undefined>;
        removeFromList: (stockNumber: number) => Promise<Shortlist | undefined>;
        removeAll: () => Promise<Shortlist | undefined>;
    };
    useSavedSearches: (
        params: PaginationParams
    ) => UseQueryResult<GetSavedSearchesResponse, unknown>;
    useSavedSearchesApi: (
        params: PaginationParams & {
            lastItemFallback: (reset?: boolean) => void;
        }
    ) => {
        error: string | null;
        saveSearches: (stockNumber: any) => Promise<SavedSearchWithMeta[] | undefined>;
        deleteSearch: (searchId: string) => Promise<void>;
        deleteAll: () => Promise<void>;
        renameSearch: (
            params: Omit<RenameSearchParams, "userContextId">
        ) => Promise<SavedSearchWithMeta | undefined>;
    };
};

export type PaginationParams = {
    page: number;
    order?: string;
};

export const FavouritesContext = createContext<FavouritesContextProviderContextProps | null>(null);

const FavouritesProvider = ({ children }: FavouritesContextProviderProps) => {
    const userContext = useUserContext();
    const { userContextId } = userContext;

    const useShortlistHook = () => useShortlist({ userContextId });
    const shortlistApi = useShortlistApi({ userContextId });

    const useSavedSearchesHook = (params: PaginationParams) =>
        useSavedSearches({ userContextId, ...params });

    const useSavedSearchesApiHook = (
        params: PaginationParams & {
            lastItemFallback: (reset?: boolean) => void;
        }
    ) => useSavedSearchesApi({ userContextId, ...params } as any);

    return (
        <FavouritesContext.Provider
            value={{
                userContext,
                useShortlist: useShortlistHook,
                shortlistApi,
                useSavedSearches: useSavedSearchesHook,
                useSavedSearchesApi: useSavedSearchesApiHook,
            }}
        >
            {children}
        </FavouritesContext.Provider>
    );
};

export default FavouritesProvider;
